import { now } from '../Clock.ts';
import {
  DEFAULT_CONFIG,
  type GameConfig,
  type GameState,
  type PipeSnapshot,
} from './GameState.ts';
import { PipeField } from './Pipes.ts';
import { circleRectOverlap, integrate, type Bird } from './Physics.ts';
import { Rng } from './Rng.ts';

/**
 * Headless, deterministic Flappy Bird simulation.
 *
 * Contract:
 *   - No DOM, no timers, no Math.random(), no wall-clock reads for physics.
 *   - Exactly one `step(flap)` call advances exactly 1 / simHz seconds.
 *   - Given the same seed and the same sequence of `flap` booleans, every run
 *     produces bit-identical state. That is what `experiments/Replay.ts` relies
 *     on, and it is why the engine is driven by a fixed-timestep accumulator in
 *     `app/App.ts` rather than by frame deltas.
 *
 * The engine has no idea whether `flap` came from a keyboard, a bot, or a
 * participant's stimulated wrist landing on a button.
 */
export class GameEngine {
  readonly config: GameConfig;
  /** Fixed timestep, seconds. */
  readonly dt: number;

  private rng: Rng;
  private pipes: PipeField;
  private bird: Bird = { y: 0, vy: 0 };

  private tick = 0;
  private aliveTicks = 0;
  /** Consecutive ticks currently overlapping a pipe, for `collisionGraceMs`. */
  private overlapTicks = 0;
  /** Simulation times of recent flaps, for the required-flap-rate readout. */
  private flapTimes: number[] = [];
  private score = 0;
  private alive = true;
  private flappedThisTick = false;
  private flapCount = 0;
  private cachedState: GameState;

  /** Wall-clock source. Injectable so headless trials can run faster than realtime. */
  now: () => number = () => now();

  constructor(config: Partial<GameConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.dt = 1 / this.config.simHz;
    this.rng = new Rng(this.config.seed);
    this.pipes = new PipeField(this.config, this.rng);
    this.reset(this.config.seed);
    this.cachedState = this.buildState();
  }

  /** Restart. Passing a seed makes the next run reproducible from that seed. */
  reset(seed: number = this.config.seed): void {
    this.config.seed = seed;
    this.rng = new Rng(seed);
    this.pipes = new PipeField(this.config, this.rng);
    this.bird = { y: this.config.height / 2, vy: 0 };
    this.tick = 0;
    this.aliveTicks = 0;
    this.score = 0;
    this.alive = true;
    this.flappedThisTick = false;
    this.flapCount = 0;
    this.overlapTicks = 0;
    this.flapTimes = [];
    this.cachedState = this.buildState();
  }

  get isAlive(): boolean {
    return this.alive;
  }

  get totalFlaps(): number {
    return this.flapCount;
  }

  get currentTick(): number {
    return this.tick;
  }

  /** Simulation clock in ms. Independent of wall clock and of frame rate. */
  get simTimeMs(): number {
    return (this.tick * 1000) / this.config.simHz;
  }

  /**
   * Advance one fixed timestep.
   *
   * @param flap true if a flap was ACTUALLY executed this tick. Note the word
   *   "executed": a bot deciding to flap does not call this. Only a detected
   *   input event -- key, button, EMG burst, or a participant's stimulated
   *   movement closing a switch -- reaches this argument.
   */
  step(flap: boolean): GameState {
    if (!this.alive) {
      this.flappedThisTick = false;
      this.cachedState = this.buildState();
      return this.cachedState;
    }

    this.flappedThisTick = flap;
    if (flap) {
      this.flapCount++;
      this.flapTimes.push(this.simTimeMs);
      if (this.flapTimes.length > 256) this.flapTimes.shift();
    }

    integrate(this.bird, this.config, this.dt, flap);
    this.pipes.update(this.dt);
    this.resolveBounds();
    this.resolveCollisions();
    this.resolveScore();

    this.tick++;
    if (this.alive) this.aliveTicks++;
    this.cachedState = this.buildState();
    return this.cachedState;
  }

  getState(): GameState {
    return this.cachedState;
  }

  /**
   * Refresh the cached state's wall-clock timestamp without advancing anything.
   *
   * The cached state was stamped when the previous step ran, which is up to one
   * tick ago. A controller about to act needs "the state as of now", and so
   * does anything measuring the interval between a detected input and the flap
   * it caused -- with a stale stamp that interval comes out negative and is
   * silently discarded from the latency statistics.
   */
  restamp(): GameState {
    this.cachedState = { ...this.cachedState, timestamp: this.now() };
    return this.cachedState;
  }

  /**
   * Collision radius. Never larger than the drawn body, so this can only ever
   * make the game more forgiving than it looks.
   */
  get collisionRadius(): number {
    return this.config.birdRadius * Math.min(1, Math.max(0.05, this.config.hitboxScale));
  }

  /** Flaps per second over the trailing window -- the stimulation budget. */
  recentFlapRate(windowSec = 5): number {
    const cutoff = this.simTimeMs - windowSec * 1000;
    let n = 0;
    for (let i = this.flapTimes.length - 1; i >= 0; i--) {
      if (this.flapTimes[i] < cutoff) break;
      n++;
    }
    const elapsed = Math.min(windowSec, this.simTimeMs / 1000);
    return elapsed > 0 ? n / elapsed : 0;
  }

  private resolveBounds(): void {
    const { height, ceilingKills } = this.config;
    const birdRadius = this.collisionRadius;
    if (this.bird.y - birdRadius <= 0) {
      if (ceilingKills) {
        this.alive = false;
        return;
      }
      this.bird.y = birdRadius;
      if (this.bird.vy < 0) this.bird.vy = 0;
    }
    if (this.bird.y + birdRadius >= height) {
      this.bird.y = height - birdRadius;
      this.bird.vy = 0;
      this.alive = false;
    }
  }

  /**
   * Pipe collisions, with a grace window.
   *
   * Overlap alone does not kill: the bird has to stay inside a pipe for
   * `collisionGraceMs` before the run ends, so a flap that arrives slightly
   * late can still pull it out. That is exactly the failure mode a stimulated
   * human actuator produces, which is why the forgiveness is temporal here
   * rather than spatial. The ground has no grace -- it is a hard floor.
   */
  private resolveCollisions(): void {
    if (!this.alive) return;
    const { birdX, pipeWidth, height, collisionGraceMs } = this.config;
    const birdRadius = this.collisionRadius;

    let overlapping = false;
    for (const pipe of this.pipes.all()) {
      if (pipe.x > birdX + birdRadius) break;
      if (pipe.x + pipeWidth < birdX - birdRadius) continue;
      if (
        circleRectOverlap(birdX, this.bird.y, birdRadius, pipe.x, 0, pipeWidth, pipe.gapTop) ||
        circleRectOverlap(
          birdX, this.bird.y, birdRadius,
          pipe.x, pipe.gapBottom, pipeWidth, height - pipe.gapBottom,
        )
      ) {
        overlapping = true;
        break;
      }
    }

    if (!overlapping) {
      this.overlapTicks = 0;
      return;
    }
    this.overlapTicks++;
    const graceTicks = Math.floor((collisionGraceMs / 1000) * this.config.simHz);
    if (this.overlapTicks > graceTicks) this.alive = false;
  }

  private resolveScore(): void {
    const { birdX, pipeWidth } = this.config;
    for (const pipe of this.pipes.all()) {
      if (!pipe.scored && pipe.x + pipeWidth < birdX) {
        pipe.scored = true;
        this.score++;
      }
    }
  }

  private buildState(): GameState {
    const { birdX, birdRadius, pipeWidth, height, gravity, drag, flapImpulse, pipeSpeed } =
      this.config;
    const next = this.pipes.next(birdX, birdRadius);
    const pipes: PipeSnapshot[] = this.pipes.snapshot();

    // If the field is momentarily empty (only possible on the first tick after a
    // reset with an extreme config) fall back to a virtual pipe far to the right
    // centred on the play field, so controllers never see NaN.
    const nextPipeX = next ? next.x : this.config.width * 2;
    const nextPipeGapTop = next ? next.gapTop : height / 2 - this.config.pipeGap / 2;
    const nextPipeGapBottom = next ? next.gapBottom : height / 2 + this.config.pipeGap / 2;

    return {
      timestamp: this.now(),
      tick: this.tick,
      simTimeMs: this.simTimeMs,
      birdY: this.bird.y,
      birdVelocityY: this.bird.vy,
      nextPipeX,
      nextPipeGapTop,
      nextPipeGapBottom,
      score: this.score,
      alive: this.alive,
      flapped: this.flappedThisTick,
      birdX,
      birdRadius,
      collisionRadius: this.collisionRadius,
      gravity,
      drag,
      flapImpulse,
      pipeSpeed,
      pipeWidth,
      height,
      pipes,
      aliveTicks: this.aliveTicks,
    };
  }
}
